import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "../ui/popover";
import { Button } from "../ui/button";
import {
  Banknote, Receipt, Package, ChefHat, Wallet, Percent,
  CheckCircle2, AlertTriangle, CircleDashed, ArrowRight, Lock, RefreshCw,
} from "lucide-react";
import api from "../../lib/api";

const STATUS_FG = {
  ok:      "text-emerald-700",
  warning: "text-amber-700",
  pending: "text-orange-700",
  missing: "text-rose-700",
  none:    "text-muted-foreground",
};

const STATUS_ICON = {
  ok: CheckCircle2,
  warning: AlertTriangle,
  pending: CircleDashed,
  missing: AlertTriangle,
  none: CircleDashed,
};

const ITEM_ICON = {
  cash:       Banknote,
  sales:      Receipt,
  inventory:  Package,
  production: ChefHat,
  expenses:   Wallet,
  food_cost:  Percent,
};

const ACTION_ROUTES = {
  open_cash:        "/app/finance?tab=closings",
  close_cash:       "/app/finance?tab=closings",
  view_cash:        "/app/finance?tab=closings",
  close_sales_day:  "/app/finance?tab=closings",
  view_sales:       "/app/finance?tab=closings",
  close_inventory:  "/app/inventory",
  view_inventory:   "/app/inventory",
  close_production: "/app/finance?tab=closings",
  view_production:  "/app/finance?tab=closings",
  review_inbox:     "/app/finance?tab=inbox",
  view_inbox:       "/app/finance?tab=inbox",
};

export default function ClosingStatusTopbarPill({ restaurantId }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    if (!restaurantId) return;
    setLoading(true);
    try {
      const r = await api.getTodayClosingStatus(restaurantId);
      setData(r);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [restaurantId]);

  useEffect(() => {
    load();
    // Auto-refresh every 5 minutes
    const id = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [load]);

  // Reload when popover opens (fresh data)
  useEffect(() => { if (open) load(); }, [open, load]);

  const progress = useMemo(() => {
    if (!data) return { done: 0, total: 5, pending: 0, warnings: 0 };
    return data.progress || { done: 0, total: 5, pending: 0, warnings: 0 };
  }, [data]);

  if (!restaurantId) return null;

  // Hide pill while panel is dormant (matches DailyClosingStatusWidget gating)
  if (data?.gating && data.gating.active === false) return null;

  const { done, total, pending, warnings } = progress;
  const hasIssues = pending > 0 || warnings > 0;
  const allClosed = data?.all_closed;

  // Pill color by overall state
  const pillClass = allClosed
    ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
    : warnings > 0
      ? "bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100"
      : pending > 0
        ? "bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100"
        : "bg-violet-50 text-violet-700 border-violet-200 hover:bg-violet-100";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          className={`hidden md:inline-flex items-center gap-1.5 h-8 px-2.5 text-[11px] font-medium border ${pillClass}`}
          data-testid="closing-status-topbar-pill"
          title="Estado del cierre de hoy"
        >
          <Lock className="h-3 w-3" />
          <span className="tabular-nums">{done}/{total}</span>
          {hasIssues && (
            <span className={`h-1.5 w-1.5 rounded-full animate-pulse ${warnings > 0 ? "bg-amber-500" : "bg-orange-500"}`} />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-80 p-0 overflow-hidden"
        align="end"
        data-testid="closing-status-topbar-popover"
      >
        <div className="bg-gradient-to-r from-violet-50 to-fuchsia-50 p-3 border-b">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-semibold text-sm text-violet-900">Cierre de hoy</div>
              <div className="text-[11px] text-violet-700 tabular-nums">{data?.day}</div>
            </div>
            <div className="flex items-center gap-2">
              <div className="text-right">
                <div className="font-bold text-lg text-violet-700 tabular-nums leading-none">{done}/{total}</div>
                {(pending > 0 || warnings > 0) && (
                  <div className="text-[10px] text-violet-600">
                    {pending > 0 && `${pending} pendiente${pending > 1 ? "s" : ""}`}
                    {pending > 0 && warnings > 0 && " · "}
                    {warnings > 0 && `${warnings} aviso${warnings > 1 ? "s" : ""}`}
                  </div>
                )}
              </div>
              <Button
                size="sm" variant="ghost"
                className="h-6 w-6 p-0 text-violet-700 hover:bg-violet-100"
                onClick={(e) => { e.stopPropagation(); load(); }}
                disabled={loading}
                data-testid="closing-status-topbar-refresh"
                title="Actualizar"
              >
                <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} />
              </Button>
            </div>
          </div>
        </div>

        {!data ? (
          <div className="p-4 text-center text-[11px] text-muted-foreground">
            {loading ? "Cargando…" : "Sin datos"}
          </div>
        ) : (
          <div className="divide-y">
            {(data.items || []).map(item => {
              const Icon = ITEM_ICON[item.id] || CircleDashed;
              const StatusIcon = STATUS_ICON[item.status] || CircleDashed;
              const fg = STATUS_FG[item.status] || STATUS_FG.none;
              const route = ACTION_ROUTES[item.action] || "/app/finance?tab=closings";
              return (
                <button
                  key={item.id}
                  className="w-full flex items-center gap-2.5 p-2.5 text-left hover:bg-gray-50 transition-colors"
                  onClick={() => {
                    setOpen(false);
                    navigate(route);
                  }}
                  data-testid={`closing-status-topbar-item-${item.id}`}
                >
                  <div className="h-7 w-7 rounded-md border bg-card flex items-center justify-center shrink-0">
                    <Icon className={`h-3.5 w-3.5 ${fg}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-[12px] font-medium text-foreground truncate">{item.label}</span>
                      <StatusIcon className={`h-3 w-3 ${fg} shrink-0`} />
                    </div>
                    <div className="text-[10px] text-muted-foreground truncate" title={item.detail}>
                      {item.detail}
                    </div>
                  </div>
                  <ArrowRight className="h-3 w-3 text-muted-foreground shrink-0" />
                </button>
              );
            })}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
